import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, MessageCircle, Loader2 } from "lucide-react";
import { waLink } from "../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadForm = ({ interest }) => {
  const [form, setForm] = useState({ nome: "", whatsapp: "", email: "", interesse: "Comprar um carro", mensagem: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (interest) {
      setForm((f) => ({ ...f, interesse: "Comprar um carro", mensagem: `Tenho interesse no ${interest.nome} (${interest.ano}).` }));
    }
  }, [interest]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.whatsapp.trim()) {
      toast.error("Preencha nome e WhatsApp.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, origem: "formulario" });
      setSent(true);
      toast.success("Recebido! Nossa equipe entra em contato em instantes.");
    } catch {
      toast.error("Erro ao enviar. Tente pelo WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  const waMessage = `Olá! Sou ${form.nome || "cliente"} e quero falar com a Seed Motors. ${form.mensagem || ""}`;

  const inputCls =
    "w-full bg-[#0d0f17] border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors duration-300";

  return (
    <section id="contato" className="py-24 sm:py-32 relative overflow-hidden" data-testid="lead-form-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(16,185,129,0.1),transparent_55%)]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-xs font-mono2 tracking-widest uppercase text-emerald-400 mb-3">05 — Contato</p>
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-white leading-tight">
            Agende sua <span className="text-emerald-400">demonstração</span> ou visite o showroom
          </h2>
          <p className="mt-5 text-slate-400 leading-relaxed max-w-md">
            Deixe seus dados e um especialista Seed Motors retorna em minutos —
            pelo canal que você preferir. Seu lead entra direto no nosso CRM e já
            chega com todo o contexto.
          </p>
          <div className="mt-8 space-y-3 text-sm text-slate-300">
            <p>Rua Santa Catarina, 122 B — Jardim Milenium, Araguari/MG</p>
            <p>CEP 38.447-023 · Seg a Sex, 9h às 19h · Sáb, 9h às 15h</p>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="glass rounded-3xl p-8 sm:p-10 space-y-4"
          data-testid="lead-form"
        >
          <input className={inputCls} placeholder="Seu nome*" value={form.nome} onChange={set("nome")} data-testid="lead-input-nome" />
          <input className={inputCls} placeholder="WhatsApp (DDD + número)*" value={form.whatsapp} onChange={set("whatsapp")} data-testid="lead-input-whatsapp" />
          <input className={inputCls} type="email" placeholder="E-mail" value={form.email} onChange={set("email")} data-testid="lead-input-email" />
          <select className={inputCls} value={form.interesse} onChange={set("interesse")} data-testid="lead-select-interesse">
            <option>Comprar um carro</option>
            <option>Vender meu carro</option>
            <option>Simular financiamento</option>
            <option>Conhecer o sistema para minha loja</option>
          </select>
          <textarea
            className={`${inputCls} min-h-[96px] resize-none`}
            placeholder="Mensagem (opcional)"
            value={form.mensagem}
            onChange={set("mensagem")}
            data-testid="lead-input-mensagem"
          />
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="submit"
              disabled={sending}
              data-testid="lead-submit-button"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-emerald-950 font-bold px-6 py-4 rounded-full transition-colors duration-300"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />}
              {sent ? "Enviado!" : "Enviar para o CRM"}
            </button>
            <a
              href={waLink(waMessage)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="lead-whatsapp-button"
              className="flex-1 inline-flex items-center justify-center gap-2 glass hover:border-emerald-500/50 text-white font-semibold px-6 py-4 rounded-full transition-colors duration-300"
            >
              <MessageCircle className="w-5 h-5 text-emerald-400" />
              Chamar no WhatsApp
            </a>
          </div>
        </motion.form>
      </div>
    </section>
  );
};
